/**
 * Shared defaults for Monitor Dashboard: typography, contrast, tooltips, legend.
 * Chart types merge these fragments with chart-specific scales and datasets.
 */

import Chart from "chart.js";

export const MONITOR_CHART_AXIS_COLOR = "#6b7280";

export const MONITOR_CHART_GRID_COLOR = "rgba(0, 0, 0, 0.12)";

export const MONITOR_CHART_TOOLTIP_BG = "rgba(33, 33, 33, 0.94)";

const ROOT_PALETTE = ["#2E0A54", "#671A99", "#9B3FC4", "#B87FD8", "#DDBBEE", "#F4F4F4", "#FFFFFF"];
const ZAIN_PALETTE = ["#0B4D2A", "#178A3E", "#69BE28", "#2FB79A", "#BEE84A", "#F4F4F4", "#FFFFFF"];
const MOBILY_PALETTE = ["#062A5A", "#0072CE", "#2E9BE8", "#7FC4F2", "#C4E4FA", "#F4F4F4", "#FFFFFF"];
const STC_PALETTE = ["#3A006B", "#8412A8", "#CF75E8", "#C71585", "#FF375E", "#F4F4F4", "#FFFFFF"];

export function reverseMonitorDashboardTimelineSeries(
    labels: string[],
    values: number[],
    aligned?: any[],
): void {
    labels.reverse();
    values.reverse();
    if (aligned) {
        aligned.reverse();
    }
}

export function resolveMonitorDashboardThemeName(): string {
    const el = document.querySelector("[theme]") as HTMLElement;
    if (!el) {
        return "root";
    }

    const attr = el.getAttribute("theme");
    if (attr) {
        return String(attr).toLowerCase();
    }

    const valueAttr = el.getAttribute("value");
    if (valueAttr) {
        return String(valueAttr).toLowerCase();
    }

    return "root";
}

export function getMonitorDashboardThemePalette(): string[] {
    const themeName = resolveMonitorDashboardThemeName();
    if (themeName === "zain") {
        return ZAIN_PALETTE;
    }
    if (themeName === "mobily") {
        return MOBILY_PALETTE;
    }
    if (themeName === "stc") {
        return STC_PALETTE;
    }
    return ROOT_PALETTE;
}

const MONITOR_CHART_COLOR_BLUE = "#1E7FE0";
const MONITOR_CHART_COLOR_GREEN = "#3AA524";
const MONITOR_CHART_COLOR_CORAL = "#FF375E";
const MONITOR_CHART_COLOR_WARM = "#FF9E5C";
const MONITOR_CHART_COLOR_VIOLET = "#7B3FB0";
const MONITOR_CHART_COLOR_EMERALD = "#00A88E";
const MONITOR_CHART_COLOR_FALLBACK = "#7B5BA8";

const CUSTOMER_TYPE_CHART_COLORS: { [key: string]: string } = {
    "1": MONITOR_CHART_COLOR_BLUE,
    "3": MONITOR_CHART_COLOR_GREEN,
    "2": MONITOR_CHART_COLOR_VIOLET,
};

const OPERATOR_CHART_COLORS: { [key: string]: string } = {
    Mobily: MONITOR_CHART_COLOR_BLUE,
    Zain: MONITOR_CHART_COLOR_GREEN,
    STC: MONITOR_CHART_COLOR_CORAL,
};

const SENDER_TYPE_CHART_COLORS: { [key: string]: string } = {
    advertisement: MONITOR_CHART_COLOR_BLUE,
    awareness: MONITOR_CHART_COLOR_GREEN,
    service: MONITOR_CHART_COLOR_WARM,
    warning: MONITOR_CHART_COLOR_VIOLET,
};

const DURATION_CHART_COLORS: { [key: string]: string } = {
    "1": MONITOR_CHART_COLOR_BLUE,
    "2": MONITOR_CHART_COLOR_GREEN,
    "3": MONITOR_CHART_COLOR_WARM,
    "4": MONITOR_CHART_COLOR_VIOLET,
    "5": MONITOR_CHART_COLOR_EMERALD,
};

const COMPLAINT_STATUS_CHART_COLORS: { [key: string]: string } = {
    "1": MONITOR_CHART_COLOR_WARM,
    "3": MONITOR_CHART_COLOR_CORAL,
    "2": MONITOR_CHART_COLOR_EMERALD,
};

function getMappedChartColor(map: { [key: string]: string }, key: string): string {
    if (Object.prototype.hasOwnProperty.call(map, key) && map[key]) {
        return map[key];
    }
    return MONITOR_CHART_COLOR_FALLBACK;
}

export function getMonitorDashboardChartColor(chartId: string, seriesKey?: string): string {
    const key = seriesKey !== undefined && seriesKey !== null && String(seriesKey) !== ""
        ? String(seriesKey)
        : "default";

    if (chartId === "allRequests" || chartId === "timelineByCustomerType" || chartId === "senderNameLengthCompliance") {
        return getMappedChartColor(CUSTOMER_TYPE_CHART_COLORS, key);
    }
    if (chartId === "sendersByNodes" || chartId === "delayedRequests") {
        return getMappedChartColor(OPERATOR_CHART_COLORS, key);
    }
    if (chartId === "requestsBySmsProviderAndSenderType") {
        return getMappedChartColor(SENDER_TYPE_CHART_COLORS, key);
    }
    if (chartId === "sendersDuration") {
        return getMappedChartColor(DURATION_CHART_COLORS, key);
    }
    if (chartId === "complaintsStatuses") {
        return getMappedChartColor(COMPLAINT_STATUS_CHART_COLORS, key);
    }
    if (chartId === "complaintFrequencyPerSender") {
        if (key === "deactivated" || key === "2") {
            return MONITOR_CHART_COLOR_EMERALD;
        }
        if (key === "falseComplaint" || key === "3") {
            return MONITOR_CHART_COLOR_CORAL;
        }
        return MONITOR_CHART_COLOR_FALLBACK;
    }
    if (chartId === "activeSendersTimeline" || chartId === "requestTimeline") {
        return MONITOR_CHART_COLOR_BLUE;
    }
    if (chartId === "senderExpirationTimeline" || chartId === "complaintsTimeline") {
        return MONITOR_CHART_COLOR_CORAL;
    }
    if (chartId === "senderRenewalTimeline") {
        return MONITOR_CHART_COLOR_GREEN;
    }
    return MONITOR_CHART_COLOR_FALLBACK;
}

export function getMonitorDashboardCssVar(name: string, fallback: string): string {
    const root = document.querySelector("[theme]") as HTMLElement;
    const el = root ? root : (document.documentElement as HTMLElement);
    const val = window.getComputedStyle(el).getPropertyValue(name);
    const v = val ? val.trim() : "";
    return v ? v : fallback;
}

export function getMonitorDashboardFontFamily(): string {
    return getMonitorDashboardCssVar("--font-type", "DIN Next LT Arabic");
}

export function monitorDashboardDarkerBorder(hex: string): string {
    if (!hex || hex.charAt(0) !== "#" || (hex.length !== 7 && hex.length !== 4)) {
        return MONITOR_CHART_AXIS_COLOR;
    }
    let r = 0;
    let g = 0;
    let b = 0;
    if (hex.length === 7) {
        r = parseInt(hex.slice(1, 3), 16);
        g = parseInt(hex.slice(3, 5), 16);
        b = parseInt(hex.slice(5, 7), 16);
    } else {
        r = parseInt(hex.slice(1, 2) + hex.slice(1, 2), 16);
        g = parseInt(hex.slice(2, 3) + hex.slice(2, 3), 16);
        b = parseInt(hex.slice(3, 4) + hex.slice(3, 4), 16);
    }
    const factor = 0.88;
    r = Math.max(0, Math.min(255, Math.floor(r * factor)));
    g = Math.max(0, Math.min(255, Math.floor(g * factor)));
    b = Math.max(0, Math.min(255, Math.floor(b * factor)));
    return "rgb(" + r + "," + g + "," + b + ")";
}

export type MonitorDashboardLegendOnClickThis = { chart?: any } & Record<string, unknown>;

export function monitorDashboardLegendClickHandler(
    this: MonitorDashboardLegendOnClickThis,
    e: any,
    legendItem: any,
): void {
    const self = this;
    const chart: any = self.chart != null ? self.chart : self;
    if (!chart) {
        return;
    }
    const index = legendItem.datasetIndex;
    const meta = chart.getDatasetMeta(index);
    meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
    chart.update();
}

export function buildMonitorDashboardLegendTop(
    fontFamily: string,
    onClick?: (e: any, legendItem: any) => void,
): any {
    return {
        display: true,
        position: "top",
        align: "center",
        labels: {
            boxWidth: 10,
            usePointStyle: true,
            fontSize: 14,
            fontColor: MONITOR_CHART_AXIS_COLOR,
            fontFamily: fontFamily,
            padding: 16,
        },
        onClick: onClick ? onClick : monitorDashboardLegendClickHandler,
    };
}

export function buildMonitorDashboardLegendTopNoClick(fontFamily: string): any {
    return {
        display: true,
        position: "top",
        align: "center",
        labels: {
            boxWidth: 10,
            usePointStyle: true,
            fontSize: 14,
            fontColor: MONITOR_CHART_AXIS_COLOR,
            fontFamily: fontFamily,
            padding: 16,
        },
    };
}

export function createMonitorDashboardLegendHeightPlugin(extraPx: number): any {
    return {
        beforeInit: function (chart: any) {
            const fitValue = chart.legend.fit;
            chart.legend.fit = function fit() {
                fitValue.bind(chart.legend)();
                this.height += extraPx;
            };
        },
    };
}

export function buildMonitorDashboardCategoryAxisTicks(fontSize: number, fontFamily: string, maxRotation?: number): any {
    const ticks: any = {
        fontSize: fontSize,
        fontColor: MONITOR_CHART_AXIS_COLOR,
        fontFamily: fontFamily,
    };
    if (maxRotation !== undefined) {
        ticks.maxRotation = maxRotation;
        ticks.minRotation = 0;
    }
    return ticks;
}

export function buildMonitorDashboardValueAxisTicks(
    fontSize: number,
    fontFamily: string,
    beginAtZero: boolean,
    maxVal?: number,
    stepSize?: number,
): any {
    const ticks: any = {
        beginAtZero: beginAtZero,
        fontSize: fontSize,
        fontColor: MONITOR_CHART_AXIS_COLOR,
        fontFamily: fontFamily,
    };
    if (maxVal !== undefined && maxVal !== null) {
        ticks.max = maxVal;
    }
    if (stepSize !== undefined && stepSize !== null) {
        ticks.stepSize = stepSize;
        ticks.autoSkip = false;
    }
    return ticks;
}

export function buildMonitorDashboardBaseTooltipStyling(): any {
    const fontFamily = getMonitorDashboardFontFamily();
    return {
        mode: "nearest",
        intersect: true,
        backgroundColor: MONITOR_CHART_TOOLTIP_BG,
        titleFontColor: "#ffffff",
        titleFontFamily: fontFamily,
        bodyFontColor: "#ffffff",
        bodyFontFamily: fontFamily,
        footerFontFamily: fontFamily,
        borderColor: MONITOR_CHART_GRID_COLOR,
        borderWidth: 1,
        xPadding: 10,
        yPadding: 8,
    };
}

export function buildMonitorDashboardResponsiveChartOptions(): any {
    ensureMonitorDashboardChartZoomSync();
    return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            datalabels: {
                display: false,
            },
        },
        animation: { duration: 0 },
    };
}

export const MONITOR_REQUESTS_TIMELINE_FALLBACK_HEIGHT_PX = 400;
export const MONITOR_REQUESTS_TIMELINE_MONTH_WIDTH_PX = 48;
export const MONITOR_REQUESTS_TIMELINE_MIN_INNER_WIDTH_PX = 600;
export const MONITOR_REQUESTS_TIMELINE_DEFAULT_VISIBLE_MONTHS = 12;
export const MONITOR_REQUESTS_TIMELINE_LAYOUT_PADDING_BOTTOM_PX = 52;

export function buildMonitorDashboardRequestsTimelineScrollStyle(barCount: number): Record<string, string> {
    const overflowX =
        barCount > MONITOR_REQUESTS_TIMELINE_DEFAULT_VISIBLE_MONTHS ? "auto" : "hidden";
    return {
        overflowX: overflowX,
        overflowY: "hidden",
        width: "100%",
        height: "100%",
    };
}

export function buildMonitorDashboardRequestsTimelineInnerStyle(
    barCount: number,
    scrollClientWidth: number,
    chartHeightPx: number,
): Record<string, string> {
    const heightPx =
        chartHeightPx > 0 ? chartHeightPx : MONITOR_REQUESTS_TIMELINE_FALLBACK_HEIGHT_PX;
    let widthPx = scrollClientWidth > 0 ? scrollClientWidth : MONITOR_REQUESTS_TIMELINE_MIN_INNER_WIDTH_PX;
    if (barCount > MONITOR_REQUESTS_TIMELINE_DEFAULT_VISIBLE_MONTHS) {
        widthPx = Math.max(
            MONITOR_REQUESTS_TIMELINE_MIN_INNER_WIDTH_PX,
            barCount * MONITOR_REQUESTS_TIMELINE_MONTH_WIDTH_PX,
        );
    }
    return {
        width: widthPx + "px",
        minWidth: widthPx + "px",
        height: heightPx + "px",
        minHeight: heightPx + "px",
    };
}

export function buildMonitorDashboardRequestsTimelineLayoutPadding(): {
    top: number;
    right: number;
    bottom: number;
    left: number;
} {
    return {
        top: 16,
        right: 8,
        bottom: MONITOR_REQUESTS_TIMELINE_LAYOUT_PADDING_BOTTOM_PX,
        left: 8,
    };
}

export function buildMonitorDashboardRequestsTimelineXAxisGridLines(): any {
    return {
        display: true,
        drawBorder: true,
        drawOnChartArea: false,
        drawTicks: true,
        color: MONITOR_CHART_GRID_COLOR,
        zeroLineColor: MONITOR_CHART_GRID_COLOR,
    };
}

export function forceMonitorDashboardChartResize(chart: any): void {
    if (!chart || typeof chart.resize !== "function") {
        return;
    }
    const canvas = chart.canvas as HTMLCanvasElement;
    if (canvas) {
        canvas.removeAttribute("width");
        canvas.removeAttribute("height");
        canvas.style.width = "";
        canvas.style.height = "";
    }
    chart.width = 0;
    chart.height = 0;
    if (chart.currentDevicePixelRatio !== undefined) {
        chart.currentDevicePixelRatio = window.devicePixelRatio || 1;
    }
    chart.resize();
}

let monitorDashboardChartZoomSyncBound = false;

function resizeAllMonitorDashboardChartInstances(): void {
    const instances = (Chart as any).instances;
    if (!instances) {
        return;
    }
    const keys = Object.keys(instances);
    for (let i = 0; i < keys.length; i++) {
        forceMonitorDashboardChartResize(instances[keys[i]]);
    }
}

export function ensureMonitorDashboardChartZoomSync(): void {
    if (monitorDashboardChartZoomSyncBound) {
        return;
    }
    monitorDashboardChartZoomSyncBound = true;
    window.addEventListener("resize", resizeAllMonitorDashboardChartInstances);
    const visualViewport = (window as any).visualViewport;
    if (visualViewport && typeof visualViewport.addEventListener === "function") {
        visualViewport.addEventListener("resize", resizeAllMonitorDashboardChartInstances);
    }
}

export function scheduleMonitorDashboardChartResize(
    chart: any,
    vueInstance: { $nextTick: (fn: () => void) => void | Promise<void> },
): void {
    vueInstance.$nextTick(function () {
        forceMonitorDashboardChartResize(chart);
    });
}
